import { push } from "connected-react-router";
import { fork, put, select, takeLatest } from "redux-saga/effects";

import { callApi } from "../../../sagas/helpers/api";
import {
  selectEnvironment,
  setError,
  setSuccess,
  startLoading,
} from "../../../store/appSlice";
import * as CashNotifications from "../api/Notifications";
import {
  createCashManagementNotificationsSettings,
  fetchCashManagementNotificationsSettings,
  notificationsOperationPaths,
  setCashManagementNotificationsSettingsActiveFlag,
  setFetchEnvironment,
  setNotificationsSettings,
  updateCashManagementNotificationsSettings,
} from "./notifications.slice";

import type { APPLICATION_ENV } from "../../../constants/environment";
import type { UUID } from "../../../utils";
import type { ObjectWithId } from "../../../utils/ObjectWithId";
import type { CashManagementNotificationSettings } from "../models/CashManagement";

function* fetchNotificationsSettingsSaga({
  payload,
}: {
  payload: ObjectWithId;
  type: string;
}) {
  const { id } = payload;
  const { details: operationPath } = notificationsOperationPaths(id);
  const environment: APPLICATION_ENV = yield select(selectEnvironment);

  yield put(startLoading(operationPath));
  yield put(setFetchEnvironment({ environment }));
  try {
    // @ts-ignore noImplicitAny
    const data = yield callApi(
      CashNotifications.getCashManagementNotificationsSettings,
      id
    );

    yield put(setNotificationsSettings({ id, item: data }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateNotificationsSettingsSaga({
  payload,
}: {
  payload: {
    id: UUID;
    data: any;
  };
  type: string;
}) {
  const { id, data } = payload;
  const { update: operationPath } = notificationsOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const notificationsSettings = yield callApi(
      CashNotifications.putCashManagementNotificationsSettings,
      id,
      data
    );

    yield put(setNotificationsSettings({ id, item: notificationsSettings }));
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/customers/${id}/cash-management`));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* createNotificationsSettingsSaga({
  payload,
}: {
  payload: {
    id: UUID;
    data: any;
  };
  type: string;
}) {
  const { id, data } = payload;

  const { create: operationPath } = notificationsOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const notificationsSettings = yield callApi(
      CashNotifications.postCashManagementNotificationsSettings,
      id,
      data
    );

    yield put(setNotificationsSettings({ id, item: notificationsSettings }));
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/customers/${id}/cash-management`));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* setNotificationsSettingsActiveFlagSaga({
  payload,
}: {
  payload: {
    id: UUID;
    data: CashManagementNotificationSettings;
  };
  type: string;
}) {
  const { id, data } = payload;

  const { active: operationPath } = notificationsOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const notificationsSettings = yield callApi(
      CashNotifications.putCashManagementNotificationsSettings,
      id,
      data
    );

    yield put(setNotificationsSettings({ id, item: notificationsSettings }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* watchFetchNotificationsSettings() {
  yield takeLatest(
    fetchCashManagementNotificationsSettings.type,
    fetchNotificationsSettingsSaga
  );
}

function* watchUpdateNotificationsSettings() {
  yield takeLatest(
    updateCashManagementNotificationsSettings.type,
    updateNotificationsSettingsSaga
  );
}

function* watchCreateNotificationsSettings() {
  yield takeLatest(
    createCashManagementNotificationsSettings.type,
    createNotificationsSettingsSaga
  );
}

function* watchNotificationsSettingsActiveFlag() {
  yield takeLatest(
    setCashManagementNotificationsSettingsActiveFlag.type,
    setNotificationsSettingsActiveFlagSaga
  );
}

export default function* rootSaga() {
  yield fork(watchFetchNotificationsSettings);
  yield fork(watchUpdateNotificationsSettings);
  yield fork(watchCreateNotificationsSettings);
  yield fork(watchNotificationsSettingsActiveFlag);
}
